// client/lib/webDesignSystem.ts
// ============================================================================
// Park Avenue — web-only global styles
// ============================================================================
//
// Three things cannot be expressed as React Native styles because they are not
// attached to any component:
//
//   1. The document background. RN styles the app root, but overscroll /
//      rubber-banding reveals the <body> behind it. On a warm-sand app that
//      flashes WHITE at the top of every scroll — a small detail that
//      immediately reads as cheap.
//   2. Keyframe animations for the ambient background. Reanimated drives the
//      component-level motion, but the ambient blobs run continuously and are
//      much cheaper as a CSS animation the compositor owns than as a JS-driven
//      loop that keeps the bridge busy.
//   3. Text selection, focus rings, and scrollbars.
//
// Deliberately NOT edited into web-build/index.html. That file is regenerated
// by `web:build:production`, and hand-edits there have silently vanished
// before — taking a Lighthouse SEO score from 100 to 63 when a stale copy was
// restored. Injecting from the bundle means the styles ship with the code that
// depends on them.

import { Platform } from "react-native";

const STYLE_ID = "fahali-park-avenue";

const CSS = `
/* Keep in sync with v3Light in client/constants/theme.ts — this is a SECOND
   copy of the same four values, reached by CSS rather than by the theme object.
   --fahali-slate was #4C4B46 while theme.textSecondary moved to the founder's
   Slate #504B43 on 2026-08-15; two surfaces showing the same tier must read the
   same value or the web build quietly disagrees with the app. */
:root {
  --font-inter: "Fahali Inter Variable";
  --fahali-sand: #F5F0E7;
  --fahali-obsidian: #0B0F1A; /* navy instrument, = V7.bg (2026-10-02) */
  --fahali-slate: #504B43;
  --fahali-gold: #A97826;
}

/* Overscroll must show the canvas, not white. */
html, body, #root {
  background-color: var(--fahali-sand);
  overflow-x: hidden;
  overscroll-behavior: none;
  height: 100%;
  min-height: 0;
}
html[data-fahali-theme="dark"],
html[data-fahali-theme="dark"] body,
html[data-fahali-theme="dark"] #root {
  background-color: var(--fahali-obsidian);
}

body {
  margin: 0;
  overflow: hidden;
  font-family: var(--font-inter), Inter, system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

/* RN Web surfaces should feel like an app canvas, not a document viewport. */
*, *::before, *::after {
  -webkit-tap-highlight-color: transparent;
}
[role="button"], button, a {
  touch-action: manipulation;
}

/*
 * The app owns scrolling, not the document. Without a finite root height,
 * React Native Web's ScrollViews expand into the page and the browser preserves
 * window.scrollY across routes. Opening a shorter screen can then show a blank
 * canvas while its content sits above the viewport.
 */
#root {
  overflow: hidden;
}

/* Selection in the brand gold rather than the browser's blue. */
::selection {
  background-color: rgba(156, 123, 54, 0.28);
  color: var(--fahali-obsidian);
}

/* Ambient intelligence. Prime-numbered periods (14s/18s/22s) so the three
   layers never phase-align — a shared period makes the background pulse in
   unison, which reads as a loading indicator rather than as atmosphere. */
@keyframes fahaliPulseGlow {
  0%   { transform: scale(1)   translate(0, 0);       opacity: 0.3; }
  50%  { transform: scale(1.1) translate(20px, -20px); opacity: 0.5; }
  100% { transform: scale(1)   translate(0, 0);       opacity: 0.3; }
}

.fahali-blob-1 { animation: fahaliPulseGlow 14s ease-in-out infinite; }
.fahali-blob-2 { animation: fahaliPulseGlow 18s ease-in-out infinite; }
.fahali-blob-3 { animation: fahaliPulseGlow 22s ease-in-out infinite; }

/* Rise from a blur, mimicking an eye focusing. Available for any web surface
   that is not already driven by Reanimated. */
@keyframes fahaliSlideUpFade {
  0%   { opacity: 0; transform: translateY(40px) scale(0.96); filter: blur(8px); }
  100% { opacity: 1; transform: translateY(0)    scale(1);    filter: blur(0px); }
}
.fahali-enter {
  animation: fahaliSlideUpFade 0.8s cubic-bezier(0.16, 1, 0.3, 1) both;
}

/* A visible focus ring is a hard accessibility requirement and the app holds
   Lighthouse A11y 100. Gold, matched to the accent, but never removed. */
:focus-visible {
  outline: 2px solid var(--fahali-gold);
  outline-offset: 2px;
  border-radius: 4px;
}

/* Slim, warm scrollbars — the default grey rail fights the cream canvas. */
* {
  scrollbar-width: thin;
  scrollbar-color: rgba(18, 18, 16, 0.22) transparent;
}
*::-webkit-scrollbar { width: 10px; height: 10px; }
*::-webkit-scrollbar-track { background: transparent; }
*::-webkit-scrollbar-thumb {
  background-color: rgba(23, 23, 20, 0.20);
  border-radius: 999px;
  border: 3px solid transparent;
  background-clip: content-box;
}
*::-webkit-scrollbar-thumb:hover { background-color: rgba(23, 23, 20, 0.34); }

/* Honour the OS setting. Ambient motion is decorative by definition, so it
   stops entirely rather than merely slowing. */
@media (prefers-reduced-motion: reduce) {
  .fahali-blob-1, .fahali-blob-2, .fahali-blob-3, .fahali-enter {
    animation: none !important;
  }
  html { scroll-behavior: auto !important; }
  *, *::before, *::after {
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
}

@media (prefers-reduced-transparency: reduce) {
  .fahali-glass-surface {
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
    background-image: none !important;
  }
}

@media (prefers-contrast: more) {
  * { scrollbar-color: rgba(23, 23, 20, 0.48) transparent; }
}
`;

let injected = false;

/**
 * Injects the global stylesheet. Idempotent and safe to call on every mount —
 * Fast Refresh remounts the app root repeatedly in development.
 */
export function installWebDesignSystem(): void {
  if (Platform.OS !== "web" || injected) return;
  if (typeof document === "undefined") return; // SSR / prerender guard

  if (document.getElementById(STYLE_ID)) {
    injected = true;
    return;
  }

  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
  injected = true;
}

/**
 * Mirrors the app's theme choice onto <html> so the document background
 * matches during overscroll. Called whenever the theme mode changes.
 */
export function syncWebThemeAttribute(mode: "light" | "dark"): void {
  if (Platform.OS !== "web") return;
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("data-fahali-theme", mode);
  // Drives the browser UI (address bar on mobile Safari/Chrome).
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "theme-color");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", mode === "dark" ? "#0B0F1A" : "#F5F0E7");
}
